<template>
  <div class="xml-viewer">
    <div
      ref="editorContainer"
      class="xml-editor-container xml-viewer"
      dir="ltr"
    />
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch, nextTick } from "vue";
import { EditorView, basicSetup } from "codemirror";
import { EditorState } from "@codemirror/state";
import { xml } from "@codemirror/lang-xml";
import { oneDark } from "@codemirror/theme-one-dark";
import { search, openSearchPanel, searchKeymap } from "@codemirror/search";
import { keymap } from "@codemirror/view";
import { defaultKeymap, history } from "@codemirror/commands";

const props = defineProps({
  xmlContent: {
    type: String,
    required: true,
  },
});

const editorContainer = ref(null);
let editor = null;

function formatXml(xmlString) {
  try {
    // Remove XML declaration and leading whitespace
    const cleaned = xmlString
      .replace(/^<\?xml[^>]*>\s*/i, "")
      .replace(/^\s+/, "");
    const parser = new window.DOMParser();
    const xmlDoc = parser.parseFromString(cleaned, "application/xml");
    const serializer = new window.XMLSerializer();
    return serializer.serializeToString(xmlDoc);
  } catch (e) {
    return xmlString;
  }
}

function createEditor(doc) {
  return new EditorView({
    state: EditorState.create({
      doc,
      extensions: [
        basicSetup,
        xml(),
        oneDark,
        EditorView.editable.of(false),
        keymap.of([...defaultKeymap, ...searchKeymap]),
        search({
          top: true,
          caseSensitive: false,
        }),
        history(),
        EditorView.theme(
          {
            "&": {
              height: "100%",
              background: "transparent",
            },
            ".cm-content": {
              fontFamily: `'Fira Code', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace`,
              fontSize: "13px",
            },
            ".cm-editor": {
              width: "fit-content !important",
            },
            ".cm-scroller": {
              minWidth: "0",
              boxSizing: "border-box",
              overflowX: "auto",
              overflowY: "auto",
              marginTop: "0",
              paddingTop: "0",
            },
            '[name="replace"], [name="replaceAll"], [name="close"]': {
              display: "none !important",
            },
          },
          { dark: true }
        ),
      ],
    }),
    parent: editorContainer.value,
  });
}

onMounted(async () => {
  editor = createEditor(formatXml(props.xmlContent));
  await nextTick();
  if (editor) {
    editor.focus();
    openSearchPanel(editor);
  }
});

onBeforeUnmount(() => {
  if (editor) editor.destroy();
});

watch(
  () => props.xmlContent,
  (newVal) => {
    if (editor) {
      editor.setState(
        EditorState.create({
          doc: formatXml(newVal),
          extensions: editor.state.facet(EditorState.extensions),
        })
      );
    }
  }
);
</script>

<style scoped>
.xml-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 18px;
}
.xml-header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}
.ltr-header {
  flex-direction: row;
  text-align: left;
}
.rtl-header {
  flex-direction: row-reverse;
  text-align: right;
}
.xml-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 500;
  white-space: nowrap;
}
.xml-editor-container {
  min-height: 66vh;
  width: fit-content;
  max-width: 35vw !important;
  height: fit-content;
  box-sizing: border-box;
  margin-top: 16px;
  background: var(--v-theme-surface-variant, #23272e);
  border: 1px solid rgba(var(--v-border-color), 0.08);
  border-radius: 12px;
  display: block;
  direction: ltr !important;
  text-align: left !important;
  overflow: auto !important; /* very important do not delete!! */
  padding: 0 !important;
  margin: 0 !important;
}
.xml-editor-container :deep(.cm-content),
.xml-editor-container :deep(.cm-scroller) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Modern, visible, gray-outlined search bar */
.xml-editor-container :deep(.cm-search) {
  background: #181818;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 6px 12px;
  font-size: 13px;
  display: flex;
  align-items: center;
  border: none;
  margin: 8px 0 0 0;
}

.xml-editor-container :deep(.cm-search input[type="text"]) {
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1.5px solid #888;
  background: #222;
  color: #fff;
  margin-right: 8px;
  min-width: 120px;
  height: 28px;
  outline: none;
  box-shadow: none;
  transition: border 0.2s;
}

.xml-editor-container :deep(.cm-search input[type="text"]:focus) {
  border: 2px solid #aaa;
  outline: none;
  box-shadow: none;
}

.xml-editor-container :deep(.cm-search button) {
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 6px;
  background: #222;
  color: #fff;
  border: 1.5px solid #888;
  margin-left: 4px;
  cursor: pointer;
  transition: background 0.2s, border 0.2s;
  box-shadow: none;
}

.xml-editor-container :deep(.cm-search button:hover) {
  background: #333;
  border: 1.5px solid #aaa;
}

.xml-editor-container :deep(.cm-search label) {
  display: flex;
  align-items: center;
  gap: 2px;
  vertical-align: middle;
  margin-bottom: 0;
}

input[type="checkbox"] {
  appearance: none;
  width: 16px;
  height: 16px;
  background-color: #181818;
  border: 2px solid #888;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  vertical-align: middle;
  transition: all 0.2s ease-in-out;
}

input[type="checkbox"]:focus {
  outline: none;
  border-color: #aaa;
}

input[type="checkbox"]:checked {
  background-color: #2196f3;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px #2196f333;
}

input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 5px;
  width: 4px;
  height: 8px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.xml-editor-container :deep(.cm-search [name="replace"]),
.xml-editor-container :deep(.cm-search [name="replaceAll"]),
.xml-editor-container :deep(.cm-search [name="close"]) {
  display: none !important;
}
</style>

<style>
.cm-editor {
  width: fit-content;
  height: fit-content;
  min-height: 60vh;
}
</style>
